import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Badge, Form, ProgressBar, Modal } from 'react-bootstrap';
import { colors } from '../../brand-colors';
import VisualWorkshop from '../../components/VisualWorkshop';
import MemeSession from '../../components/MemeSession';

// Section definitions matching backend
const SECTION_DEFS = [
  { key: 'marketing_objectives', title: 'Marketing Objectives & Channel Alignment' },
  { key: 'audience_channel_map', title: 'Audience & Content Testing Framework' },
  { key: 'meme_hierarchy', title: 'Meme Hierarchy', special: true },
  { key: 'visual_standards', title: 'Visual Production Standards' },
  { key: 'channel_behavior', title: 'Channel Behavior Rules' },
  { key: 'hashtag_architecture', title: 'Hashtag & Tagging Architecture' },
  { key: 'first_comment', title: 'First-Comment Strategy' },
  { key: 'content_cadence', title: 'Content Cadence & Sequencing' },
  { key: 'cta_standards', title: 'CTA Standards' },
  { key: 'social_proof', title: 'Social Proof & Authority Deployment' },
  { key: 'campaign_integration', title: 'Campaign Integration Rules' },
  { key: 'anti_patterns', title: 'Anti-Patterns (Hard Rules)' },
  { key: 'measurement', title: 'Measurement & Success Metrics' },
];

// Platform tone options for question 1 — each with a short description
const PLATFORM_TONES = {
  LinkedIn: [
    { value: 'Professional', desc: 'Polished, credibility-forward, industry language' },
    { value: 'Thought-leader', desc: 'Bold takes, frameworks, challenge conventional thinking' },
    { value: 'Conversational', desc: 'Approachable, like talking to a peer over coffee' },
    { value: 'Personal', desc: 'First-person stories, behind-the-scenes, vulnerable' },
  ],
  'FB/X/Threads': [
    { value: 'Casual', desc: 'Relaxed, informal, scroll-stopping' },
    { value: 'Witty', desc: 'Sharp humor, clever hooks, personality-forward' },
    { value: 'Direct', desc: 'Punchy, no fluff, straight to the point' },
    { value: 'Storytelling', desc: 'Mini-narratives, anecdotes, pulls you in' },
  ],
  Email: [
    { value: 'Personal', desc: 'First-person, behind-the-scenes, "just you and me"' },
    { value: 'To-the-point', desc: 'Concise, one idea per email, respects their time' },
    { value: 'Advisory', desc: '"Here\'s what I\'d do" — guidance from experience' },
    { value: 'Story-driven', desc: 'Opens with an anecdote, narrative arc' },
  ],
  Blog: [
    { value: 'Trend Interpreter', desc: 'Translate what\'s emerging into business impact' },
    { value: 'How-to / Tutorial', desc: 'Step-by-step guides, practical instruction' },
    { value: 'Conversational', desc: 'Casual tone, like explaining to a friend' },
    { value: 'Deep-dive', desc: 'Thorough exploration, frameworks, methodology' },
    { value: 'Opinionated', desc: 'Takes stances, contrarian angles, personality-forward' },
  ],
};

// ---------------------------------------------------------------------------
// Utility: safe text rendering
// ---------------------------------------------------------------------------
function safeText(val) {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) return val.map(safeText).join(', ');
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

// ---------------------------------------------------------------------------
// EditForm: human-friendly editor for playbook section content
// ---------------------------------------------------------------------------
function EditForm({ content, onChange }) {
  const humanLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const handleFieldChange = (key, value) => {
    onChange({ ...content, [key]: value });
  };

  // Fallback for non-object content
  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    return (
      <Form.Control
        as="textarea"
        rows={10}
        value={typeof content === 'string' ? content : JSON.stringify(content, null, 2)}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value)); } catch { /* let them keep typing */ }
        }}
      />
    );
  }

  return (
    <div>
      {Object.entries(content).map(([key, value]) => {
        const label = humanLabel(key);

        // Simple string
        if (typeof value === 'string') {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              <Form.Control
                as="textarea"
                rows={Math.min(Math.max(Math.ceil(value.length / 60), 2), 6)}
                value={value}
                onChange={(e) => handleFieldChange(key, e.target.value)}
              />
            </div>
          );
        }

        // Number / boolean
        if (typeof value === 'number' || typeof value === 'boolean') {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              <Form.Control
                value={String(value)}
                onChange={(e) => {
                  const num = Number(e.target.value);
                  handleFieldChange(key, isNaN(num) ? e.target.value : num);
                }}
              />
            </div>
          );
        }

        // Array of strings
        if (Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')) {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              {value.map((item, i) => (
                <div key={i} className="d-flex gap-2 mb-1">
                  <Form.Control
                    size="sm"
                    value={item}
                    onChange={(e) => {
                      const updated = [...value];
                      updated[i] = e.target.value;
                      handleFieldChange(key, updated);
                    }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => handleFieldChange(key, value.filter((_, j) => j !== i))}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-1"
                onClick={() => handleFieldChange(key, [...value, ''])}
              >
                + Add Item
              </Button>
            </div>
          );
        }

        // Array of objects
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              {value.map((item, i) => (
                <div key={i} className="border rounded p-2 mb-2" style={{ backgroundColor: '#f8f9fa' }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">Item {i + 1}</small>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      style={{ padding: '0 6px', fontSize: '0.75rem' }}
                      onClick={() => handleFieldChange(key, value.filter((_, j) => j !== i))}
                    >
                      Remove
                    </Button>
                  </div>
                  {Object.entries(item).map(([subKey, subVal]) => (
                    <div key={subKey} className="mb-1">
                      <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: colors.greyMid }}>
                        {humanLabel(subKey)}
                      </label>
                      {typeof subVal === 'string' ? (
                        <Form.Control
                          as="textarea"
                          size="sm"
                          rows={Math.min(Math.max(Math.ceil(String(subVal).length / 60), 1), 3)}
                          value={subVal}
                          onChange={(e) => {
                            const updated = [...value];
                            updated[i] = { ...item, [subKey]: e.target.value };
                            handleFieldChange(key, updated);
                          }}
                        />
                      ) : Array.isArray(subVal) && subVal.every(v => typeof v === 'string') ? (
                        <div>
                          {subVal.map((sv, si) => (
                            <div key={si} className="d-flex gap-1 mb-1">
                              <Form.Control
                                size="sm"
                                value={sv}
                                onChange={(e) => {
                                  const updatedSub = [...subVal];
                                  updatedSub[si] = e.target.value;
                                  const updated = [...value];
                                  updated[i] = { ...item, [subKey]: updatedSub };
                                  handleFieldChange(key, updated);
                                }}
                              />
                              <Button
                                variant="outline-danger"
                                size="sm"
                                style={{ padding: '0 4px', fontSize: '0.7rem' }}
                                onClick={() => {
                                  const updatedSub = subVal.filter((_, sj) => sj !== si);
                                  const updated = [...value];
                                  updated[i] = { ...item, [subKey]: updatedSub };
                                  handleFieldChange(key, updated);
                                }}
                              >
                                x
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ padding: '0 6px', fontSize: '0.7rem' }}
                            className="mt-1"
                            onClick={() => {
                              const updated = [...value];
                              updated[i] = { ...item, [subKey]: [...subVal, ''] };
                              handleFieldChange(key, updated);
                            }}
                          >
                            + Add
                          </Button>
                        </div>
                      ) : (
                        <Form.Control
                          size="sm"
                          value={safeText(subVal)}
                          onChange={(e) => {
                            const updated = [...value];
                            updated[i] = { ...item, [subKey]: e.target.value };
                            handleFieldChange(key, updated);
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-1"
                onClick={() => {
                  const template = {};
                  Object.keys(value[0]).forEach(k => {
                    const v = value[0][k];
                    template[k] = Array.isArray(v) ? [] : '';
                  });
                  handleFieldChange(key, [...value, template]);
                }}
              >
                + Add {label.replace(/s$/, '')}
              </Button>
            </div>
          );
        }

        // Plain object
        if (typeof value === 'object' && value !== null) {
          return (
            <div key={key} className="mb-3">
              <label className="form-label fw-bold">{label}</label>
              <div className="border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
                {Object.entries(value).map(([subKey, subVal]) => (
                  <div key={subKey} className="mb-2">
                    <label className="form-label mb-0" style={{ fontSize: '0.8rem', color: colors.greyMid }}>
                      {humanLabel(subKey)}
                    </label>
                    {typeof subVal === 'string' ? (
                      <Form.Control
                        as="textarea"
                        size="sm"
                        rows={Math.min(Math.max(Math.ceil(String(subVal).length / 60), 1), 3)}
                        value={subVal}
                        onChange={(e) => {
                          handleFieldChange(key, { ...value, [subKey]: e.target.value });
                        }}
                      />
                    ) : Array.isArray(subVal) && subVal.every(v => typeof v === 'string') ? (
                      <div>
                        {subVal.map((sv, si) => (
                          <div key={si} className="d-flex gap-1 mb-1">
                            <Form.Control
                              size="sm"
                              value={sv}
                              onChange={(e) => {
                                const updatedSub = [...subVal];
                                updatedSub[si] = e.target.value;
                                handleFieldChange(key, { ...value, [subKey]: updatedSub });
                              }}
                            />
                            <Button
                              variant="outline-danger"
                              size="sm"
                              style={{ padding: '0 4px', fontSize: '0.7rem' }}
                              onClick={() => {
                                handleFieldChange(key, { ...value, [subKey]: subVal.filter((_, sj) => sj !== si) });
                              }}
                            >
                              x
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ padding: '0 6px', fontSize: '0.7rem' }}
                          className="mt-1"
                          onClick={() => {
                            handleFieldChange(key, { ...value, [subKey]: [...subVal, ''] });
                          }}
                        >
                          + Add
                        </Button>
                      </div>
                    ) : (
                      <Form.Control
                        size="sm"
                        value={safeText(subVal)}
                        onChange={(e) => {
                          handleFieldChange(key, { ...value, [subKey]: e.target.value });
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        }

        // Fallback
        return (
          <div key={key} className="mb-3">
            <label className="form-label fw-bold">{label}</label>
            <Form.Control
              value={safeText(value)}
              onChange={(e) => handleFieldChange(key, e.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
}

function BrandPlaybook() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sections, setSections] = useState({});
  const [staleAlerts, setStaleAlerts] = useState([]);
  const [status, setStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [activeSection, setActiveSection] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(null);
  const [showObjectivesReview, setShowObjectivesReview] = useState(false);
  const [objectivesUpdateText, setObjectivesUpdateText] = useState('');

  // Intake flow state
  const [showIntake, setShowIntake] = useState(false);
  const [intakeStep, setIntakeStep] = useState(0);
  const [showWorkshop, setShowWorkshop] = useState(false);
  const [showMemeSession, setShowMemeSession] = useState(false);
  const [existingMeme, setExistingMeme] = useState(null);
  const [answers, setAnswers] = useState({
    channel_personality: {},
    hashtag_approach: 'Essential',
    content_rhythm: '',
    anti_patterns: '',
    social_proof_comfort: '',
    visual_workshop: null,
  });

  const sectionRefs = useRef({});

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------
  useEffect(() => {
    Promise.all([
      fetch('/api/brand-playbook/sections', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/brand-playbook/status', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/brand-playbook/answers', { credentials: 'include' }).then(r => r.json()),
      fetch('/api/brand-playbook/meme/context', { credentials: 'include' }).then(r => r.json()),
    ]).then(([sectionsData, statusData, answersData, memeData]) => {
      if (!sectionsData.error) {
        setSections(sectionsData.sections || sectionsData);
        if (sectionsData.stale_alerts) setStaleAlerts(sectionsData.stale_alerts);
      }
      setStatus(statusData);
      if (answersData.answers) {
        setAnswers(prev => ({ ...prev, ...answersData.answers }));
      }
      if (memeData.existing_meme) {
        setExistingMeme(memeData.existing_meme);
      }
      setLoading(false);
    }).catch(err => {
      setError('Failed to load playbook data');
      setLoading(false);
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Generate playbook
  // ---------------------------------------------------------------------------
  const handleGenerate = async (excludeKeys = []) => {
    setShowIntake(false);
    setGenerating(true);
    setGenProgress(0);
    setError('');

    try {
      // Save answers first
      await fetch('/api/brand-playbook/answers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ answers }),
      });

      // Generate sections
      const res = await fetch('/api/brand-playbook/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ exclude_keys: excludeKeys }),
      });

      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        // Refresh sections
        const sectionsRes = await fetch('/api/brand-playbook/sections', { credentials: 'include' });
        const sectionsData = await sectionsRes.json();
        if (!sectionsData.error) {
          setSections(sectionsData.sections || sectionsData);
          if (sectionsData.stale_alerts) setStaleAlerts(sectionsData.stale_alerts);
        }

        // Refresh status
        const statusRes = await fetch('/api/brand-playbook/status', { credentials: 'include' });
        const statusData = await statusRes.json();
        setStatus(statusData);

        if (data.total_errors > 0) {
          setError(`Generated ${data.total_generated} sections with ${data.total_errors} error(s)`);
        }
      }
    } catch (err) {
      setError('Failed to generate playbook');
    }
    setGenerating(false);
  };

  // ---------------------------------------------------------------------------
  // Regenerate single section
  // ---------------------------------------------------------------------------
  const handleRegenerateSection = async (key) => {
    setRegeneratingKey(key);
    try {
      const res = await fetch('/api/brand-playbook/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ section_key: key }),
      });
      const data = await res.json();
      if (data.results && data.results[key]) {
        setSections(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            content: data.results[key].content,
            human_edited: false,
            generated_at: new Date().toISOString(),
          }
        }));
      }
    } catch (err) {
      setError(`Failed to regenerate section`);
    }
    setRegeneratingKey(null);
  };

  // ---------------------------------------------------------------------------
  // Save edits
  // ---------------------------------------------------------------------------
  const handleSaveEdit = async () => {
    if (!editingSection || !editContent) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/brand-playbook/sections/${editingSection}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: editContent }),
      });
      const data = await res.json();
      if (!data.error) {
        setSections(prev => ({
          ...prev,
          [editingSection]: {
            ...prev[editingSection],
            content: editContent,
            human_edited: true,
            edited_at: data.edited_at,
          }
        }));
      }
    } catch (err) {
      setError('Failed to save edits');
    }
    setSaving(false);
    setEditingSection(null);
    setEditContent(null);
  };

  // ---------------------------------------------------------------------------
  // Scroll to section
  // ---------------------------------------------------------------------------
  const scrollToSection = (key) => {
    setActiveSection(key);
    const el = sectionRefs.current[key];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---------------------------------------------------------------------------
  // Has sections?
  // ---------------------------------------------------------------------------
  const hasPlaybook = status?.has_playbook;
  const sectionCount = Object.keys(sections).length;

  // ---------------------------------------------------------------------------
  // Intake flow
  // ---------------------------------------------------------------------------
  const renderIntakeFlow = () => {
    const steps = [
      { title: 'Channel Personality', key: 'channel_personality' },
      { title: 'Hashtag Approach', key: 'hashtag_approach' },
      { title: 'Content Rhythm', key: 'content_rhythm' },
      { title: 'Anti-Patterns', key: 'anti_patterns' },
      { title: 'Social Proof Comfort', key: 'social_proof_comfort' },
    ];

    const currentStep = steps[intakeStep];
    const isLast = intakeStep === steps.length - 1;

    return (
      <Card className="shadow-sm mb-4">
        <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em' }}>
          <strong>Playbook Setup — Step {intakeStep + 1} of {steps.length}</strong>
        </Card.Header>
        <Card.Body className="p-4">
          <ProgressBar
            now={((intakeStep + 1) / steps.length) * 100}
            className="mb-4"
            style={{ height: '6px' }}
          />

          {/* Step 1: Channel Personality */}
          {currentStep.key === 'channel_personality' && (
            <>
              <h5 className="mb-3">How should your brand voice adapt per platform?</h5>
              <p className="text-muted mb-4">Select up to 2 per platform. Your base voice stays the same — these dial up specific qualities. Same voice, different execution.</p>
              {Object.entries(PLATFORM_TONES).map(([platform, tones]) => (
                <div key={platform} className="mb-4">
                  <strong className="d-block mb-2">{platform}</strong>
                  <div className="d-flex flex-wrap gap-2">
                    {tones.map(({ value: tone, desc }) => {
                      const selected = (answers.channel_personality[platform] || []).includes(tone);
                      const atMax = (answers.channel_personality[platform] || []).length >= 2 && !selected;
                      return (
                        <div key={tone} className="d-flex flex-column align-items-center" style={{ maxWidth: '160px' }}>
                          <Button
                            size="sm"
                            variant={selected ? 'dark' : 'outline-secondary'}
                            disabled={atMax}
                            className="w-100"
                            onClick={() => {
                              setAnswers(prev => {
                                const current = prev.channel_personality[platform] || [];
                                const updated = selected
                                  ? current.filter(t => t !== tone)
                                  : [...current, tone];
                                return {
                                  ...prev,
                                  channel_personality: {
                                    ...prev.channel_personality,
                                    [platform]: updated,
                                  }
                                };
                              });
                            }}
                            style={selected ? { backgroundColor: colors.greyDarkest, borderColor: colors.greyDarkest, color: colors.goldLight } : {}}
                          >
                            {tone}
                          </Button>
                          <small className="text-muted text-center mt-1" style={{ fontSize: '0.72rem', lineHeight: '1.2' }}>{desc}</small>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Step 2: Hashtag Approach */}
          {currentStep.key === 'hashtag_approach' && (
            <>
              <h5 className="mb-3">Hashtag Strategy</h5>
              <Alert variant="info" style={{ backgroundColor: '#1E3A5F', color: '#fff', border: 'none' }}>
                <strong>Recommended: Essential.</strong> Branded hashtags build discoverability and brand recognition over time.
                They help your audience find all your content on a topic and make your IP frameworks searchable.
              </Alert>
              <div className="d-flex flex-wrap gap-2 mt-3">
                {['Essential', 'Occasional', 'Avoid'].map(opt => (
                  <Button
                    key={opt}
                    variant={answers.hashtag_approach === opt ? 'dark' : 'outline-secondary'}
                    onClick={() => setAnswers(prev => ({ ...prev, hashtag_approach: opt }))}
                    style={answers.hashtag_approach === opt ? { backgroundColor: colors.greyDarkest, borderColor: colors.greyDarkest, color: colors.goldLight } : {}}
                  >
                    {opt}
                    {opt === 'Essential' && ' (Recommended)'}
                  </Button>
                ))}
              </div>
            </>
          )}

          {/* Step 3: Content Rhythm */}
          {currentStep.key === 'content_rhythm' && (
            <>
              <h5 className="mb-3">Content Rhythm</h5>
              <p className="text-muted">This shapes how topics flow over time — whether your content builds arcs or stays varied. It drives the sequencing engine that suggests your next topics.</p>
              <Form.Control
                as="textarea"
                rows={4}
                value={answers.content_rhythm}
                onChange={(e) => setAnswers(prev => ({ ...prev, content_rhythm: e.target.value }))}
                placeholder="e.g., I like to do 3-post deep dives on a topic then switch. I enjoy building up to a big reveal..."
              />
              <small className="text-muted mt-1 d-block">Examples: "I like hammering a theme for a week then shifting" / "Mix it up constantly" / "Build a 3-post arc: provoke, explain, prove"</small>
            </>
          )}

          {/* Step 4: Anti-Patterns */}
          {currentStep.key === 'anti_patterns' && (
            <>
              <h5 className="mb-3">What You Never Do</h5>
              <p className="text-muted">These become hard guardrails — the AI will never produce content that violates these rules. Be specific — vague answers like "nothing generic" are hard to enforce. The more concrete, the better the guardrails.</p>
              <Form.Control
                as="textarea"
                rows={5}
                value={answers.anti_patterns}
                onChange={(e) => setAnswers(prev => ({ ...prev, anti_patterns: e.target.value }))}
                placeholder={"Examples:\n- No fake urgency timers or countdown clocks\n- No stock photos of people smiling at laptops\n- Never say 'unlock your potential' or 'level up'\n- No mainstream/obvious topic angles — bleeding edge only\n- No guilt-driven re-engagement emails"}
              />
              <small className="text-muted mt-1 d-block">
                <strong>Be specific.</strong> Instead of "no common stuff" → "no topics everyone else is covering — I want bleeding edge, not mainstream takes."
                Instead of "nothing boring" → "no listicles, no '5 tips' format, no safe opinions."
              </small>
            </>
          )}

          {/* Step 5: Social Proof Comfort */}
          {currentStep.key === 'social_proof_comfort' && (
            <>
              <h5 className="mb-3">Social Proof Comfort</h5>
              <p className="text-muted">This controls how aggressively the AI references client results and outcomes in your posts, emails, and articles. Your credentials (certifications, awards, experience) live in your bio and sales pages automatically — they're not affected by this setting.</p>
              <div className="d-flex flex-wrap gap-3 mt-3">
                {[
                  { value: 'let-work-speak', label: 'Let my work speak for itself', desc: 'Rarely mention results — the content quality is the proof' },
                  { value: 'selective', label: 'Share when relevant', desc: 'Reference outcomes when they naturally support the point being made' },
                  { value: 'active', label: 'Actively deploy proof', desc: 'Weave results and case studies into content regularly' },
                ].map(opt => (
                  <div key={opt.value} className="d-flex flex-column" style={{ maxWidth: '200px' }}>
                    <Button
                      variant={answers.social_proof_comfort === opt.value ? 'dark' : 'outline-secondary'}
                      onClick={() => setAnswers(prev => ({ ...prev, social_proof_comfort: opt.value }))}
                      style={answers.social_proof_comfort === opt.value ? { backgroundColor: colors.greyDarkest, borderColor: colors.greyDarkest, color: colors.goldLight } : {}}
                    >
                      {opt.label}
                    </Button>
                    <small className="text-muted text-center mt-1" style={{ fontSize: '0.72rem', lineHeight: '1.2' }}>{opt.desc}</small>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Navigation buttons */}
          <div className="d-flex justify-content-between mt-4 pt-3" style={{ borderTop: `1px solid ${colors.greyLight}` }}>
            <Button
              variant="outline-secondary"
              onClick={() => intakeStep > 0 ? setIntakeStep(intakeStep - 1) : setShowIntake(false)}
            >
              {intakeStep > 0 ? '← Back' : 'Cancel'}
            </Button>
            <Button
              variant="outline-dark"
              onClick={() => {
                if (isLast) {
                  // Find edited sections to exclude
                  const editedKeys = Object.entries(sections)
                    .filter(([, s]) => s.human_edited)
                    .map(([k]) => k);
                  handleGenerate(editedKeys);
                } else {
                  setIntakeStep(intakeStep + 1);
                }
              }}
            >
              {isLast ? 'Generate Playbook →' : 'Next →'}
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // ---------------------------------------------------------------------------
  // Section renderer
  // ---------------------------------------------------------------------------
  const renderSectionContent = (key, content) => {
    if (!content) return <p className="text-muted">No content generated yet.</p>;

    // Generic JSON renderer — render tables, lists, key-value pairs
    return (
      <div className="playbook-section-content">
        {Object.entries(content).map(([fieldKey, value]) => (
          <div key={fieldKey} className="mb-3">
            <strong className="d-block mb-1" style={{ textTransform: 'capitalize' }}>
              {fieldKey.replace(/_/g, ' ')}
            </strong>
            {renderValue(value)}
          </div>
        ))}
      </div>
    );
  };

  const renderValue = (value) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>;
    if (typeof value === 'string') return <p className="mb-0">{value}</p>;
    if (typeof value === 'number') return <span>{value}</span>;
    if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;

    if (Array.isArray(value)) {
      // Array of strings
      if (value.length > 0 && typeof value[0] === 'string') {
        return (
          <ul className="mb-0">
            {value.map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        );
      }
      // Array of objects — choose table vs cards based on content density
      if (value.length > 0 && typeof value[0] === 'object') {
        // Force naming/identifier columns to the front
        const priorityCols = ['platform', 'tier', 'channel', 'context', 'dimension', 'name', 'strategy'];
        const rawKeys = Object.keys(value[0]);
        const keys = [
          ...priorityCols.filter(k => rawKeys.includes(k)),
          ...rawKeys.filter(k => !priorityCols.includes(k)),
        ];

        // Identify the label key (first priority col found, or first key)
        const labelKey = priorityCols.find(k => rawKeys.includes(k)) || rawKeys[0];
        const detailKeys = keys.filter(k => k !== labelKey);

        // If multiple cells have long content, use card layout instead of table
        // Single long cell is fine in a table — cards are for when most cells are prose
        const longCellCount = value.reduce((count, row) =>
          count + detailKeys.filter(k => {
            const v = row[k];
            const str = Array.isArray(v) ? v.join(', ') : String(v ?? '');
            return str.length > 150;
          }).length, 0
        );
        const hasLongContent = longCellCount >= value.length * 2;

        if (hasLongContent) {
          // Card layout — one card per item
          return (
            <div className="d-flex flex-column gap-3">
              {value.map((row, i) => (
                <div key={i} style={{ border: `1px solid ${colors.greyLight}`, borderRadius: '6px', overflow: 'hidden' }}>
                  <div style={{
                    backgroundColor: colors.greyDarkest,
                    color: colors.goldLight,
                    padding: '8px 14px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                  }}>
                    {String(row[labelKey] ?? `Item ${i + 1}`)}
                  </div>
                  <div style={{ padding: '12px 14px' }}>
                    {detailKeys.map(k => {
                      const v = row[k];
                      if (v === null || v === undefined || v === '') return null;
                      return (
                        <div key={k} className="mb-2">
                          <strong style={{ textTransform: 'capitalize', fontSize: '0.85rem', color: colors.greyDark }}>
                            {k.replace(/_/g, ' ')}
                          </strong>
                          <div style={{ fontSize: '0.9rem', marginTop: '2px' }}>
                            {Array.isArray(v)
                              ? <ul className="mb-0 ps-3">{v.map((item, j) => <li key={j}>{String(item)}</li>)}</ul>
                              : typeof v === 'object' && v !== null
                                ? renderValue(v)
                                : String(v)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        }

        // Standard table for compact data
        return (
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.9rem' }}>
              <thead style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
                <tr>
                  {keys.map(k => (
                    <th key={k} style={{ textTransform: 'capitalize', padding: '6px 10px' }}>
                      {k.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {value.map((row, i) => (
                  <tr key={i}>
                    {keys.map(k => (
                      <td key={k} style={{ padding: '6px 10px' }}>
                        {Array.isArray(row[k])
                          ? row[k].join(', ')
                          : typeof row[k] === 'object' && row[k] !== null
                            ? JSON.stringify(row[k])
                            : String(row[k] ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      return <span>{JSON.stringify(value)}</span>;
    }

    // Nested object
    if (typeof value === 'object') {
      return (
        <div className="ps-3" style={{ borderLeft: `2px solid ${colors.greyLight}` }}>
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="mb-2">
              <strong style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}: </strong>
              {typeof v === 'string' || typeof v === 'number'
                ? <span>{String(v)}</span>
                : renderValue(v)}
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  // ---------------------------------------------------------------------------
  // Edit modal
  // ---------------------------------------------------------------------------
  const renderEditModal = () => (
    <Modal
      show={!!editingSection}
      onHide={() => { setEditingSection(null); setEditContent(null); }}
      size="lg"
    >
      <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
        <Modal.Title style={{ fontSize: '1rem', fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.05em' }}>
          Edit: {SECTION_DEFS.find(s => s.key === editingSection)?.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {editContent && (
          <EditForm
            content={editContent}
            onChange={setEditContent}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => { setEditingSection(null); setEditContent(null); }}>
          Cancel
        </Button>
        <Button variant="outline-dark" onClick={handleSaveEdit} disabled={saving}>
          {saving ? <><Spinner size="sm" className="me-1" /> Saving...</> : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  // ---------------------------------------------------------------------------
  // Loading / error states
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading playbook...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4" style={{ maxWidth: '1200px' }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 style={{
            fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
            letterSpacing: '0.15em',
            fontSize: '1.6rem',
          }}>
            Marketing Execution Playbook
          </h1>
          <p className="text-muted mb-0">
            Production standards for consistent, on-brand marketing across every channel.
          </p>
        </div>
        <div className="d-flex gap-2">
          {hasPlaybook && (
            <>
              <Button
                variant="outline-dark"
                size="sm"
                onClick={() => setShowMemeSession(true)}
                disabled={generating || showMemeSession}
              >
                {existingMeme ? 'Edit Meme Hierarchy' : 'Meme Session'}
              </Button>
              <Button
                variant="outline-dark"
                size="sm"
                onClick={() => {
                  if (answers.visual_workshop?.completed) {
                    setShowWorkshop(true);
                  } else {
                    setShowWorkshop(true);
                  }
                }}
                disabled={generating}
              >
                Regenerate
              </Button>
            </>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      {/* Stale warning */}
      {status?.stale && (
        <Alert variant="warning" className="d-flex align-items-center justify-content-between">
          <span><strong>Brand data has changed.</strong> {status.stale_reason}</span>
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => { setShowWorkshop(true); }}
          >
            Regenerate
          </Button>
        </Alert>
      )}

      {/* Stale playbook alerts (e.g. 90-day marketing objectives) */}
      {staleAlerts.length > 0 && staleAlerts.map((alert, i) => (
        <Alert key={i} variant="warning" className="d-flex align-items-center justify-content-between">
          <span>{alert.message}</span>
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => {
              setObjectivesUpdateText('');
              setShowObjectivesReview(true);
            }}
          >
            Review
          </Button>
        </Alert>
      ))}

      {/* Generating state */}
      {generating && (
        <Card className="shadow-sm mb-4">
          <Card.Body className="text-center p-5">
            <Spinner animation="border" style={{ color: colors.goldMid }} className="mb-3" />
            <h5>Generating your Marketing Execution Playbook...</h5>
            <p className="text-muted">This will take a few minutes. Generating 12 sections from your brand data and intake answers.</p>
          </Card.Body>
        </Card>
      )}

      {/* No playbook yet — show generate button */}
      {!hasPlaybook && !generating && !showIntake && !showWorkshop && (
        <Card className="shadow-sm mb-4">
          <Card.Body className="text-center p-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
            <h4>No Playbook Generated Yet</h4>
            <p className="text-muted mb-4">
              Start with the Visual Workshop to define your image style, then answer 5 questions
              about your marketing preferences. We'll generate your complete Marketing Execution
              Playbook — production standards for every channel, content type, and campaign.
            </p>
            <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
              <strong>Prerequisite:</strong> Complete your Brand Strategy Guide first — the Playbook builds on your archetypes, voice, and design data.
            </p>
            <Button
              className="btn-brand-primary"
              size="lg"
              onClick={() => { setShowWorkshop(true); }}
            >
              Generate Playbook →
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Visual Workshop (Step 0 — runs before text intake) */}
      {showWorkshop && !showIntake && !generating && (
        <VisualWorkshop
          onComplete={(workshopResults) => {
            setAnswers(prev => ({ ...prev, visual_workshop: workshopResults }));
            setShowWorkshop(false);
            setShowIntake(true);
            setIntakeStep(0);
          }}
          onSkip={() => {
            setShowWorkshop(false);
            setShowIntake(true);
            setIntakeStep(0);
          }}
          existingResults={answers.visual_workshop}
        />
      )}

      {/* Intake flow (5 text questions — after workshop) */}
      {showIntake && !generating && renderIntakeFlow()}

      {/* Meme Hierarchy creative session */}
      {showMemeSession && (
        <MemeSession
          onComplete={(memeData) => {
            setExistingMeme(memeData);
            setShowMemeSession(false);
          }}
          onCancel={() => setShowMemeSession(false)}
          existingMeme={existingMeme}
        />
      )}

      {/* Playbook content */}
      {hasPlaybook && !generating && !showMemeSession && (
        <Row>
          {/* Table of contents sidebar */}
          <Col md={3} className="d-none d-md-block">
            <Card className="shadow-sm" style={{ position: 'sticky', top: '1rem' }}>
              <Card.Header style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight, fontSize: '0.85rem', padding: '8px 12px' }}>
                <strong>Sections</strong>
              </Card.Header>
              <Card.Body className="p-0">
                {SECTION_DEFS.map(({ key, title, special }) => {
                  const hasMeme = key === 'meme_hierarchy' && existingMeme;
                  const hasSection = special ? hasMeme : sections[key];
                  return (
                    <div
                      key={key}
                      onClick={() => key === 'meme_hierarchy' ? setShowMemeSession(true) : scrollToSection(key)}
                      style={{
                        padding: '8px 12px',
                        fontSize: '0.82rem',
                        cursor: 'pointer',
                        borderLeft: activeSection === key ? `3px solid ${colors.goldLight}` : '3px solid transparent',
                        backgroundColor: activeSection === key ? '#f8f9fa' : 'transparent',
                        color: hasSection ? colors.greyDarkest : colors.greyMid,
                      }}
                    >
                      {hasSection && <span style={{ color: '#28a745', marginRight: '4px' }}>&#10003;</span>}
                      {title}
                      {sections[key]?.human_edited && (
                        <Badge bg="secondary" className="ms-1" style={{ fontSize: '0.65rem' }}>edited</Badge>
                      )}
                    </div>
                  );
                })}
              </Card.Body>
            </Card>
          </Col>

          {/* Section cards */}
          <Col md={9}>
            {SECTION_DEFS.map(({ key, title, special }) => {
              // Meme Hierarchy is stored separately, not as a BrandPlaybookSection
              if (key === 'meme_hierarchy') {
                return (
                  <div key={key} ref={el => sectionRefs.current[key] = el} className="mb-4">
                    <Card className="shadow-sm">
                      <Card.Header className="d-flex justify-content-between align-items-center" style={{
                        backgroundColor: colors.greyDarkest,
                        color: colors.goldLight,
                        fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                        letterSpacing: '0.05em',
                        fontSize: '0.95rem',
                      }}>
                        <span>{title}</span>
                        <Button
                          size="sm"
                          variant="outline-light"
                          style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                          onClick={() => setShowMemeSession(true)}
                        >
                          {existingMeme ? 'Edit' : 'Start Meme Session'}
                        </Button>
                      </Card.Header>
                      <Card.Body>
                        {existingMeme ? (
                          <>
                            {existingMeme.epimeme && (
                              <div className="mb-3">
                                <h6 style={{ color: colors.goldDark, fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>EpiMeme</h6>
                                <p style={{ fontWeight: 600, fontSize: '1.1rem', marginBottom: '0.25rem' }}>"{existingMeme.epimeme.concept}"</p>
                                <p className="text-muted" style={{ fontSize: '0.85rem' }}>{existingMeme.epimeme.rationale}</p>
                              </div>
                            )}
                            {existingMeme.product_memes && existingMeme.product_memes.length > 0 && (
                              <div className="mb-3">
                                <h6 style={{ color: colors.goldDark, fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product Memes</h6>
                                {existingMeme.product_memes.map((pm, i) => (
                                  <div key={i} className={i < existingMeme.product_memes.length - 1 ? 'mb-2 pb-2 border-bottom' : ''}>
                                    <strong>{pm.product_name}:</strong> "{pm.concept}"
                                    <br /><span className="text-muted" style={{ fontSize: '0.8rem' }}>{pm.rationale}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {existingMeme.campaign_metaphors && existingMeme.campaign_metaphors.length > 0 && (
                              <div>
                                <h6 style={{ color: colors.goldDark, fontWeight: 600, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Campaign Metaphors</h6>
                                {existingMeme.campaign_metaphors.map((cm, i) => (
                                  <div key={i} className={i < existingMeme.campaign_metaphors.length - 1 ? 'mb-2 pb-2 border-bottom' : ''}>
                                    <strong>{cm.product_name}:</strong> {cm.metaphor}
                                    <br /><span className="text-muted" style={{ fontSize: '0.8rem' }}>{cm.description}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <p className="text-muted mb-0">
                            Not created yet.{' '}
                            <Button variant="link" className="p-0" onClick={() => setShowMemeSession(true)}>
                              Start the Meme Hierarchy creative session
                            </Button>
                          </p>
                        )}
                      </Card.Body>
                    </Card>
                  </div>
                );
              }

              const section = sections[key];

              return (
                <div key={key} ref={el => sectionRefs.current[key] = el} className="mb-4">
                  <Card className="shadow-sm">
                    <Card.Header className="d-flex justify-content-between align-items-center" style={{
                      backgroundColor: colors.greyDarkest,
                      color: colors.goldLight,
                      fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif",
                      letterSpacing: '0.05em',
                      fontSize: '0.95rem',
                    }}>
                      <span>
                        {title}
                        {section?.human_edited && (
                          <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.65rem' }}>edited</Badge>
                        )}
                      </span>
                      <div className="d-flex gap-1">
                        {section && (
                          <>
                            <Button
                              size="sm"
                              variant="outline-light"
                              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                              onClick={() => {
                                setEditingSection(key);
                                setEditContent(JSON.parse(JSON.stringify(section.content)));
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-light"
                              style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                              onClick={() => handleRegenerateSection(key)}
                              disabled={regeneratingKey === key}
                            >
                              {regeneratingKey === key ? <Spinner size="sm" /> : 'Regenerate'}
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {section
                          ? renderSectionContent(key, section.content)
                          : (
                            <p className="text-muted mb-0">
                              Not generated yet.{' '}
                              <Button
                                variant="link"
                                className="p-0"
                                onClick={() => handleRegenerateSection(key)}
                                disabled={regeneratingKey === key}
                              >
                                Generate this section
                              </Button>
                            </p>
                          )
                      }
                    </Card.Body>
                  </Card>
                </div>
              );
            })}
          </Col>
        </Row>
      )}

      {/* Edit modal */}
      {renderEditModal()}

      {/* Marketing Objectives Review Modal */}
      <Modal show={showObjectivesReview} onHide={() => setShowObjectivesReview(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title style={{ fontFamily: "'Trajan Pro', 'Trajan Pro 3', 'Times New Roman', serif", letterSpacing: '0.1em', fontSize: '1rem' }}>
            Marketing Objectives Check-in
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {sections.marketing_objectives?.content && (() => {
            const obj = sections.marketing_objectives.content;
            return (
              <>
                <p className="text-muted mb-3">Your marketing objectives were last set {staleAlerts[0]?.days_old || 90}+ days ago. Take a moment to review:</p>
                <Card className="mb-3">
                  <Card.Body>
                    <strong>Current Focus:</strong> {obj.current_focus || 'Not set'}
                    {obj.focus_rationale && <p className="text-muted mt-1 mb-2" style={{ fontSize: '0.85rem' }}>{obj.focus_rationale}</p>}
                    {obj.channel_priorities && (
                      <>
                        <strong className="d-block mt-2">Channel Priorities:</strong>
                        <ul className="mb-0 mt-1" style={{ fontSize: '0.85rem' }}>
                          {obj.channel_priorities.map((ch, i) => (
                            <li key={i}><strong>{ch.channel}</strong> ({ch.priority}) — {ch.kpi}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </Card.Body>
                </Card>
                <hr />
                <p className="mb-2"><strong>Has your marketing focus changed?</strong></p>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={objectivesUpdateText}
                  onChange={(e) => setObjectivesUpdateText(e.target.value)}
                  placeholder="e.g., Shifting from lead generation to revenue — launching my core product next month. Or leave blank if nothing has changed."
                />
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={async () => {
              // Confirm still valid — reset the 90-day clock
              try {
                await fetch('/api/brand-playbook/sections/marketing_objectives/confirm', {
                  method: 'POST',
                  credentials: 'include',
                });
                setStaleAlerts([]);
                setShowObjectivesReview(false);
              } catch (err) {
                setError('Failed to confirm objectives');
              }
            }}
          >
            Still on track
          </Button>
          <Button
            variant="dark"
            disabled={!objectivesUpdateText.trim()}
            style={{ backgroundColor: colors.greyDarkest, borderColor: colors.greyDarkest }}
            onClick={async () => {
              setShowObjectivesReview(false);
              // Save the update note into answers so the regeneration prompt has it
              const updatedAnswers = { ...answers, objectives_update: objectivesUpdateText.trim() };
              await fetch('/api/brand-playbook/answers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ answers: updatedAnswers }),
              });
              setAnswers(updatedAnswers);
              // Regenerate the marketing_objectives section
              await handleRegenerateSection('marketing_objectives');
              setStaleAlerts([]);
            }}
          >
            Update & Regenerate
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default BrandPlaybook;
